// Angular Files
import {
    AbstractControl,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';

// Teller Online Library Files
import { tenantTodayIsoDate } from 'teller-online-libraries/shared';

const MONTH_LABELS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Serializes a Date (e.g. from MatDatePicker, which emits a local
 * midnight Date for the calendar day the user picked) into the
 * canonical wire format "YYYY-MM-DD". Reads LOCAL accessors
 * deliberately — switching to `getUTC*` here would re-introduce the
 * ±1-day drift this whole pattern exists to prevent (PROD-1465).
 * The user's selected calendar day must equal the wire calendar day.
 */
export function dateToYyyyMmDd(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * Inverse of dateToYyyyMmDd. Constructs a LOCAL-midnight Date from a
 * "YYYY-MM-DD" string — `new Date(yr, mo-1, dy)`, NOT `new Date(value)`.
 * The latter parses ISO date-only strings as UTC midnight, which shifts
 * the calendar day in any non-UTC browser TZ (the original PROD-1465
 * bug). Suitable for MatDatePicker [value]/[min]/[max] bindings.
 * Returns null when input is empty or malformed.
 */
export function yyyyMmDdToDate(value: string | null | undefined): Date | null {
    if (!value || !ISO_DATE_REGEX.test(value)) {
        return null;
    }

    const [yearStr, monthStr, dayStr] = value.split('-');

    return new Date(
        Number(yearStr),
        Number(monthStr) - 1,
        Number(dayStr)
    );
}

/**
 * Formats a "YYYY-MM-DD" string as "Apr 28, 2026" without any
 * timezone math — the displayed calendar day is the wire calendar
 * day, period.
 */
export function formatYyyyMmDd(value: string | null | undefined): string {
    if (!value || !ISO_DATE_REGEX.test(value)) {
        return '';
    }

    const [yearStr, monthStr, dayStr] = value.split('-');
    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    const day = Number(dayStr);

    return `${MONTH_LABELS[monthIndex]} ${day}, ${year}`;
}

/**
 * Returns "YYYY-MM-DD" for tomorrow's calendar day in the tenant's
 * timezone. DST-, month-end-, and year-end-safe via local-Date arithmetic
 * on the tenant-today value (yyyyMmDdToDate produces local-midnight;
 * setDate(...+1) advances exactly one calendar day across boundaries).
 */
export function tenantTomorrowIsoDate(tellerSiteTimeZoneId: string): string {
    const today = yyyyMmDdToDate(tenantTodayIsoDate(tellerSiteTimeZoneId))!;
    today.setDate(today.getDate() + 1);

    return dateToYyyyMmDd(today);
}

/**
 * Validator factory. Rejects calendar days earlier than max(tenantToday,
 * minDate). Both sides are "YYYY-MM-DD" strings, so lexicographic
 * comparison is calendar-correct. `getTenantToday` is a thunk so the
 * validator stays pure (no Intl/service coupling) and so the caller
 * controls the TZ source — same pattern as `getMinDate`.
 */
export const pastDateValidator = (
    getMinDate: () => string | undefined,
    getTenantToday: () => string
): ValidatorFn => (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const selected = controlValueToIsoDate(control.value);
        const today = getTenantToday();
        const minDate = getMinDate();
        const earliestAllowed = minDate && minDate > today ? minDate : today;

        return selected < earliestAllowed ? { pastDate: true } : null;
    };

/**
 * Validator factory. Rejects calendar days on or before the current
 * StartDate when the schedule is recurring.
 */
export const dateRangeValidator = (
    getContext: () => {
        startDate: string | null | undefined,
        isRecurring: boolean
    }
): ValidatorFn => (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const { startDate, isRecurring } = getContext();

        if (!isRecurring || !startDate) {
            return null;
        }

        const start = controlValueToIsoDate(startDate);
        const end = controlValueToIsoDate(control.value);

        return end <= start ? { dateRange: true } : null;
    };

/**
 * Validator factory. Rejects calendar days later than maxDate.
 */
export const maxDateValidator = (
    getMaxDate: () => string | undefined
): ValidatorFn => (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const maxDate = getMaxDate();

        if (!maxDate) {
            return null;
        }

        const selected = controlValueToIsoDate(control.value);

        return selected > maxDate ? { afterMaxDate: true } : null;
    };

/**
 * Validator factory. Rejects when the last projected payment instance
 * exceeds maxDate for recurring schedules. Relies on backend-projected
 * instance dates (the preview) rather than reimplementing interval
 * enumeration on the client.
 */
export const lastInstanceValidator = (
    getContext: () => {
        maxDate: string | undefined,
        payments: { date: string }[] | undefined,
        isRecurring: boolean
    }
): ValidatorFn => (): ValidationErrors | null => {
        const { maxDate, payments, isRecurring } = getContext();

        if (!maxDate || !payments?.length || !isRecurring) {
            return null;
        }

        const lastDate = payments[payments.length - 1].date;

        return lastDate > maxDate
            ? { lastInstanceAfterMaxDate: true }
            : null;
    };

/**
 * Coerces whatever the form control or external context surfaces into
 * a "YYYY-MM-DD" string. MatDatePicker can emit a Date; programmatic
 * setValue paths can set the canonical string directly. Strings that
 * are NOT already canonical (e.g. a stringified Date that round-tripped
 * through localStorage as a full ISO datetime) are re-derived via
 * dateToYyyyMmDd so the wire never carries a time component.
 */
export function controlValueToIsoDate(value: unknown): string {
    if (typeof value === 'string') {
        if (ISO_DATE_REGEX.test(value)) {
            return value;
        }

        return dateToYyyyMmDd(new Date(value));
    }

    if (value instanceof Date) {
        return dateToYyyyMmDd(value);
    }

    return dateToYyyyMmDd(new Date(value as string | number));
}
